import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { 
  Close as CloseIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  PhotoCamera as PhotoCameraIcon,
  Check as CheckIcon,
  TextIncrease as TextIncreaseIcon,
  TextDecrease as TextDecreaseIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useThemeMode, THEME_MODES } from '../theme/ThemeProvider';
import { useThemePreferences } from '../user-preferences/themes/useThemePreferences';
import { useFontSize } from '../hooks/useFontSize';
import UserPhotoUpload from './UserPhotoUpload';
import userPreferencesService from '../user-preferences/services/userPreferencesService';
import api from '../services/api';
import { getHeaders } from '../utils/apiUtils';

const SettingsModal = ({ open, onClose, onPhotoUpdate }) => {
  const theme = useTheme();
  const { user: currentUser, isAuthenticated } = useAuth();
  const { changeTheme, currentTheme } = useThemeMode();
  
  // Hook de preferências de tema
  const {
    userPreferences,
    updateTheme
  } = useThemePreferences(currentUser?.id);

  // Hook de tamanho da fonte
  const {
    fontSize,
    changeFontSize,
    getFontSizeLabel,
    FONT_SIZE_LABELS
  } = useFontSize();

  // Estados do modal
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Estados das configurações
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [userPhoto, setUserPhoto] = useState(null);

  // Estados para troca de senha
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [loadingSenha, setLoadingSenha] = useState(false);

  // Carregar configurações quando o modal abrir
  useEffect(() => {
    if (open && isAuthenticated && currentUser?.id) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated, currentUser?.id]);

  // Recarregar configurações quando as preferências mudarem
  useEffect(() => {
    if (userPreferences) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences]);

  // Carregar configurações do usuário
  const loadSettings = async () => {
    try {
      if (userPreferences?.theme_mode) {
        setSelectedTheme(userPreferences.theme_mode);
      } else if (currentTheme) {
        setSelectedTheme(currentTheme);
      }

      // carrega foto persistida em preferences (localStorage/API)
      if (userPreferences?.user_photo) {
        setUserPhoto(userPreferences.user_photo);
      }
    } catch (error) {
      // erro silencioso
    }
  };

  // Função para lidar com mudança de aba
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Função para selecionar tema (salva imediatamente)
  const handleThemeSelect = async (themeKey) => {
    try {
      
      // Aplicar tema imediatamente
      setSelectedTheme(themeKey);
      changeTheme(themeKey);
      
      // Salvar preferência do usuário imediatamente
      if (currentUser?.id) {
        const success = await updateTheme(themeKey);
        
        if (success) {
          
          // Feedback de sucesso
          setShowSuccess(true);
          setSuccessMessage(`Tema ${getThemeDisplayName(themeKey)} aplicado e salvo com sucesso!`);
        } else {
          throw new Error('Falha ao salvar tema');
        }
      }
      
    } catch (error) {
      
      setShowSuccess(true);
      setSuccessMessage('Erro ao salvar tema. Tente novamente.');
    }
  };

  // Função para obter nome de exibição do tema
  const getThemeDisplayName = (themeKey) => {
    const names = {
      [THEME_MODES.DEFAULT]: 'Padrão',
      [THEME_MODES.DARK]: 'Escuro',
      [THEME_MODES.PASTEL]: 'Pastel',
      [THEME_MODES.CITRICAS]: 'Cítricas',
      [THEME_MODES.VERMELHAS]: 'Vermelhas',
      [THEME_MODES.PRETO_BRANCO]: 'Preto e Branco'
    };
    return names[themeKey] || themeKey;
  };

  // Função para obter cor de destaque do tema
  const getThemeColor = (themeKey) => {
    const colors = {
      [THEME_MODES.DEFAULT]: '#1976D2', // Azul
      [THEME_MODES.DARK]: '#42A5F5', // Azul claro para dark mode
      [THEME_MODES.PASTEL]: '#E91E63', // Rosa pastel
      [THEME_MODES.CITRICAS]: '#FB8C00', // Laranja
      [THEME_MODES.VERMELHAS]: '#D32F2F', // Vermelho
      [THEME_MODES.PRETO_BRANCO]: '#616161' // Cinza médio
    };
    return colors[themeKey] || '#1976D2';
  };

  // Função para obter cores do tema para preview
  const getThemePreviewColors = (themeKey) => {
    const themeColors = {
      [THEME_MODES.DEFAULT]: {
        primary: '#1976D2',
        secondary: '#00BCD4',
        background: '#F5F5F5'
      },
      [THEME_MODES.DARK]: {
        primary: '#42A5F5',
        secondary: '#26C6DA',
        background: '#121212'
      },
      [THEME_MODES.PASTEL]: {
        primary: '#E91E63',
        secondary: '#4DB6AC',
        background: '#fefefe'
      },
      [THEME_MODES.CITRICAS]: {
        primary: '#FB8C00',
        secondary: '#FFD54F',
        background: '#FFF8E1'
      },
      [THEME_MODES.VERMELHAS]: {
        primary: '#D32F2F',
        secondary: '#E91E63',
        background: '#FFEBEE'
      },
      [THEME_MODES.PRETO_BRANCO]: {
        primary: '#000000',
        secondary: '#424242',
        background: '#FFFFFF'
      }
    };
    return themeColors[themeKey] || themeColors[THEME_MODES.DEFAULT];
  };

  // Função para lidar com mudança de foto
  const handlePhotoChange = async (newPhotoData) => {
    const photoUrl = newPhotoData?.photoUrl || null;
    setUserPhoto(photoUrl);

    // Persistir nas preferências (API ou localStorage)
    if (currentUser?.id && photoUrl) {
      await userPreferencesService.saveUserPhoto(currentUser.id, photoUrl, {
        fileName: newPhotoData.fileName,
        mimeType: newPhotoData.mimeType,
        uploadDate: newPhotoData.uploadDate
      });
      // Recarrega preferências para refletir imediatamente
      await loadSettings();
    }

    if (onPhotoUpdate) {
      onPhotoUpdate(photoUrl);
    }

    setShowSuccess(true);
    setSuccessMessage('Foto do perfil atualizada com sucesso!');
  };

  // Função para lidar com mudança de tamanho da fonte
  const handleFontSizeChange = (newSize) => {
    changeFontSize(newSize);
    setShowSuccess(true);
    setSuccessMessage(`Tamanho da fonte alterado para ${getFontSizeLabel(newSize)}!`);
  };

  // Função para fechar o modal
  const handleClose = () => {
    setActiveTab(0);
    setShowSuccess(false);
    setShowError(false);
    // Limpar campos de senha ao fechar
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    onClose();
  };

  // Função para trocar senha
  const handleChangePassword = async () => {
    // Validações
    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setShowError(true);
      setErrorMessage('Todos os campos são obrigatórios.');
      return;
    }

    if (novaSenha.length < 6) {
      setShowError(true);
      setErrorMessage('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setShowError(true);
      setErrorMessage('As senhas não coincidem.');
      return;
    }

    if (senhaAtual === novaSenha) {
      setShowError(true);
      setErrorMessage('A nova senha deve ser diferente da senha atual.');
      return;
    }

    try {
      setLoadingSenha(true);
      setShowError(false);

      const headers = getHeaders();
      // Chamar API para alterar senha
      await api.patch('/usuarios/me/senha', {
        senhaAtual,
        novaSenha
      }, { headers });

      // Limpar campos após sucesso
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');

      setShowSuccess(true);
      setSuccessMessage('Senha alterada com sucesso!');
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setShowError(true);
      setErrorMessage(
        error.response?.data?.mensagem ||
        error.response?.data?.error ||
        error.mensagem ||
        error.error ||
        'Erro ao alterar senha. Tente novamente.'
      );
    } finally {
      setLoadingSenha(false);
    }
  };

  // Verificar se o usuário está autenticado
  if (!isAuthenticated || !currentUser) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: '900px',
            height: '550px',
            maxWidth: '900px',
            maxHeight: '550px',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        {/* Header Padrão */}
        <DialogTitle
          sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
            padding: '16px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 500
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon sx={{ fontSize: '1.5rem', color: '#4caf50' }} />
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.25rem' }}>
              Preferências do Sistema
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Conteúdo com Abas */}
        <DialogContent sx={{ p: 0, height: '360px', overflow: 'auto' }}>
          {/* Sistema de Abas */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: '48px'
                }
              }}
            >
              <Tab 
                icon={<PaletteIcon sx={{ fontSize: '1rem', color: '#ff9800' }} />}
                iconPosition="start"
                label="Temas" 
              />
              <Tab 
                icon={<TextIncreaseIcon sx={{ fontSize: '1rem', color: '#9c27b0' }} />}
                iconPosition="start"
                label="Fonte" 
              />
              <Tab 
                icon={<PhotoCameraIcon sx={{ fontSize: '1rem', color: '#2196f3' }} />}
                iconPosition="start"
                label="Foto do Perfil" 
              />
              <Tab 
                icon={<LockIcon sx={{ fontSize: '1rem', color: '#f44336' }} />}
                iconPosition="start"
                label="Trocar Senha" 
              />
            </Tabs>
          </Box>

          {/* Conteúdo das Abas */}
          <Box sx={{ p: 2, minHeight: '300px' }}>
            {/* Aba 1: Temas */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Escolha seu tema preferido
                </Typography>
                
                <Grid container spacing={2}>
                  {Object.values(THEME_MODES).map((themeKey) => (
                    <Grid item xs={12} sm={6} md={4} key={themeKey}>
                      <Card
                        sx={{
                          border: selectedTheme === themeKey ? `2px solid ${getThemeColor(themeKey)}` : '1px solid #e0e0e0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <CardActionArea onClick={() => handleThemeSelect(themeKey)}>
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  backgroundColor: getThemeColor(themeKey)
                                }}
                              />
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {getThemeDisplayName(themeKey)}
                              </Typography>
                              {selectedTheme === themeKey && (
                                <CheckIcon sx={{ color: getThemeColor(themeKey), ml: 'auto' }} />
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                              {(() => {
                                const colors = getThemePreviewColors(themeKey);
                                return (
                                  <>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 1,
                                        backgroundColor: colors.primary
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 1,
                                        backgroundColor: colors.secondary
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: 1,
                                        backgroundColor: colors.background,
                                        border: '1px solid #e0e0e0'
                                      }}
                                    />
                                  </>
                                );
                              })()}
                            </Box>
                            
                            {selectedTheme === themeKey && (
                              <Chip
                                label="Ativo"
                                size="small"
                                sx={{
                                  backgroundColor: getThemeColor(themeKey),
                                  color: 'white',
                                  fontSize: '0.75rem'
                                }}
                              />
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Aba 2: Configurações de Fonte */}
            {activeTab === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Ajuste o tamanho da fonte
                </Typography>
                
                <Grid container spacing={2}>
                  {Object.entries(FONT_SIZE_LABELS).map(([key, label]) => {
                    const colors = {
                      small: '#757575',
                      medium: '#4caf50',
                      large: '#ff9800',
                      xlarge: '#f44336'
                    };
                    const icons = {
                      small: <TextDecreaseIcon />,
                      medium: <TextIncreaseIcon />,
                      large: <TextIncreaseIcon />,
                      xlarge: <TextIncreaseIcon />
                    };
                    
                    return (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      <Card
                        sx={{
                          border: fontSize === key ? `2px solid ${colors[key]}` : '1px solid #e0e0e0',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <CardActionArea onClick={() => handleFontSizeChange(key)}>
                          <CardContent sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                              {icons[key]}
                              <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                  fontWeight: 500,
                                  fontSize: key === 'small' ? '0.75rem' :
                                           key === 'medium' ? '0.875rem' :
                                           key === 'large' ? '1rem' : '1.125rem'
                                }}
                              >
                                {label}
                              </Typography>
                              {fontSize === key && (
                                <CheckIcon sx={{ color: colors[key], ml: 'auto' }} />
                              )}
                            </Box>
                            
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                fontSize: key === 'small' ? '0.7rem' :
                                         key === 'medium' ? '0.8rem' :
                                         key === 'large' ? '0.9rem' : '1rem'
                              }}
                            >
                              Texto de exemplo
                            </Typography>
                            
                            {fontSize === key && (
                              <Chip
                                label="Ativo"
                                size="small"
                                sx={{
                                  backgroundColor: colors[key],
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  mt: 1
                                }}
                              />
                            )}
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                    );
                  })}
                </Grid>
                
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Dica:</strong> O tamanho da fonte será aplicado a toda a interface do sistema. 
                    A configuração será salva automaticamente e aplicada na próxima vez que você acessar o sistema.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Aba 3: Foto do Perfil */}
            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Gerencie sua foto de perfil
                </Typography>
                <UserPhotoUpload 
                  userId={currentUser?.id}
                  currentPhoto={userPhoto}
                  onPhotoChange={handlePhotoChange}
                  size={120}
                />
                <Typography variant="body2" color="text.secondary">
                  Formatos aceitos: JPG, PNG ou WebP (máximo 5MB)
                </Typography>
              </Box>
            )}

            {/* Aba 4: Trocar Senha */}
            {activeTab === 3 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '500px', mx: 'auto' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Alterar senha de acesso
                </Typography>

                <TextField
                  fullWidth
                  label="Senha Atual"
                  type={showSenhaAtual ? 'text' : 'password'}
                  value={senhaAtual}
                  onChange={(e) => setSenhaAtual(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                          edge="end"
                        >
                          {showSenhaAtual ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Nova Senha"
                  type={showNovaSenha ? 'text' : 'password'}
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  helperText="Mínimo de 6 caracteres"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowNovaSenha(!showNovaSenha)}
                          edge="end"
                        >
                          {showNovaSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirmar Nova Senha"
                  type={showConfirmarSenha ? 'text' : 'password'}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  error={confirmarSenha !== '' && novaSenha !== confirmarSenha}
                  helperText={confirmarSenha !== '' && novaSenha !== confirmarSenha ? 'As senhas não coincidem' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                          edge="end"
                        >
                          {showConfirmarSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleChangePassword}
                    disabled={loadingSenha || !senhaAtual || !novaSenha || !confirmarSenha}
                    startIcon={loadingSenha ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 500
                    }}
                  >
                    {loadingSenha ? 'Alterando...' : 'Alterar Senha'}
                  </Button>
                </Box>

                <Box sx={{ mt: 1, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Dica de segurança:</strong> Use uma senha forte com pelo menos 6 caracteres, 
                    combinando letras, números e caracteres especiais. Evite usar informações pessoais.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer Padrão */}
        <DialogActions
          sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            height: '60px',
            padding: '8px 16px',
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px'
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de Sucesso */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          icon={<CheckIcon />}
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar de Erro */}
      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsModal;
